'use client'
import { useCallback, useMemo } from 'react'

// Constants
import { withProductContextProvider } from '@src/quince-site-common/src/contexts/product-context'
import CUSTOM_SECTION_TYPE from '@src/quince-site-common/src/domains/CustomSectionType'
import Product from '@src/quince-site-common/src/domains/Product'
import Bundle from '@src/quince-site-common/src/domains/Product/BundleProduct'
import ProductCard from '@src/quince-site-common/src/domains/ProductCard'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import PageBuilder, { ContainerConfig } from '@src/quince-site-common/src/organisms/pageBuilder'
import type { BundlePageContext } from '@src/quince-site-pdp/src/domains/BundlePageContext'

import ProductPageContextProvider from '../../contexts/ProductPageContextProvider'
import { SectionDividerProvider } from '../../contexts/section-divider-context'
import { useSectionDividersInfo } from '../../layouts/section-dividers'
import { TPDPSectionData } from '../../types/pdp-section-data.type'
import { ProductSectionType } from '../../types/productPage.types'
import { getDesktopContainerConfig, getMobileContainerConfig } from '../productPage/productPage.utils'

import { CUSTOM_SECTION_TYPES_VS_RENDERER, SECTION_TYPES_VS_RENDERER } from './bundlePage.constants'

const useContainerRenderer = (
  bundle: Bundle,
  sectionData: TPDPSectionData,
  recommendedContentfulProductIds: string[] | undefined,
  mobileContainerConfig?: ContainerConfig,
  recommendedProductCards?: ProductCard[],
  mergeProducts?: Product[],
) => {
  return useCallback(
    (props: { sectionType: ProductSectionType; customSectionType: CUSTOM_SECTION_TYPE }) => {
      const sectionType: ProductSectionType = props.sectionType
      const Section = SECTION_TYPES_VS_RENDERER[sectionType]
      if (!Section) {
        if (sectionType === ProductSectionType.CUSTOM_SECTION) {
          const customSectionType: CUSTOM_SECTION_TYPE = props.customSectionType
          const CustomSection = CUSTOM_SECTION_TYPES_VS_RENDERER[customSectionType]
          if (!CustomSection) {
            return <></>
          }
          return (
            <CustomSection
              bundle={bundle}
              recommendedContentfulProductIds={recommendedContentfulProductIds}
              recommendedProductCards={recommendedProductCards}
              mergeProducts={mergeProducts}
              sectionData={sectionData}
              mobileContainerConfig={mobileContainerConfig}
            />
          )
        }
        return <></>
      }
      return (
        <Section
          bundle={bundle}
          recommendedContentfulProductIds={recommendedContentfulProductIds}
          recommendedProductCards={recommendedProductCards}
          mergeProducts={mergeProducts}
          sectionData={sectionData}
          mobileContainerConfig={mobileContainerConfig}
        />
      )
    },
    [
      bundle,
      mergeProducts,
      mobileContainerConfig,
      recommendedContentfulProductIds,
      recommendedProductCards,
      sectionData,
    ],
  )
}

function BundlePage({ pageData }: { pageData: BundlePageContext }) {
  if (!pageData) {
    // eslint-disable-next-line i18next/no-literal-string
    throw new Error('Page data is not defined please check build script')
  }

  const containerRenderer = useContainerRenderer(
    pageData.bundle,
    pageData.sectionData,
    pageData.recommendedContentfulProductIds,
    pageData.mobileContainerConfig,
    pageData.recommendedProductCards,
    pageData.mergeProducts,
  )
  const deviceInfo = useDeviceInfo()
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
  const desktopContainerConfig = getDesktopContainerConfig(pageData.desktopContainerConfig, true)
  const mobileContainerConfig = getMobileContainerConfig(pageData.mobileContainerConfig, true)
  const sectionDividers = useSectionDividersInfo(
    isMobile ? mobileContainerConfig : desktopContainerConfig,
    pageData.bundle,
    pageData.sectionData,
  )
  const updatePageDataContext: BundlePageContext = useMemo(
    () => ({
      bundle: pageData.bundle,
      sectionData: pageData.sectionData,
      mobileContainerConfig: mobileContainerConfig,
      desktopContainerConfig: desktopContainerConfig,
      recommendedContentfulProductIds: pageData.recommendedContentfulProductIds,
    }),
    [pageData, mobileContainerConfig, desktopContainerConfig],
  )

  return (
    <ProductPageContextProvider>
      <SectionDividerProvider value={sectionDividers}>
        <PageBuilder context={updatePageDataContext} selfContainerRenderer={containerRenderer} isMobile={isMobile} />
      </SectionDividerProvider>
    </ProductPageContextProvider>
  )
}

export default withProductContextProvider(BundlePage)
